import { Host, h } from "@stencil/core";
import { updateDirectionInImageCustomVar } from "../../common/utils";
import { getControlRegisterProperty } from "../../common/registry-properties";
const DATA_IMAGE = "data-ch-image";
let GET_IMAGE_PATH_CALLBACK_REGISTRY;
/**
 * A control to display multiple images, depending on the state (focus, hover,
 * active or disabled) of a parent element.
 */
export class ChImage {
    constructor() {
        this.containerRef = undefined;
        this.disabled = false;
        this.getImagePathCallback = undefined;
        this.src = undefined;
        this.type = "background";
    }
    #currentContainerRef;
    #image;
    el;
    containerRefChanged(newContainer) {
        // Remove previous data in the current parent
        this.#currentContainerRef?.removeAttribute(DATA_IMAGE);
        this.#currentContainerRef = newContainer ?? this.el.parentElement;
        // Add data in the new parent
        this.#currentContainerRef?.setAttribute(DATA_IMAGE, "");
    }
    srcChanged(newSrc) {
        const getImagePathCallback = this.getImagePathCallback ?? GET_IMAGE_PATH_CALLBACK_REGISTRY;
        if (!getImagePathCallback) {
            this.#image = null;
            return;
        }
        const image = getImagePathCallback(newSrc);
        if (!image) {
            this.#image = null;
            return;
        }
        this.#image = newSrc
            ? updateDirectionInImageCustomVar(image, "start")
            : undefined;
    }
    connectedCallback() {
        // Initialize default getImagePathCallback
        GET_IMAGE_PATH_CALLBACK_REGISTRY ??= getControlRegisterProperty("getImagePathCallback", "ch-image");
        this.#currentContainerRef = this.containerRef ?? this.el.parentElement;
        this.#currentContainerRef?.setAttribute(DATA_IMAGE, "");
        if (this.src) {
            this.srcChanged(this.src);
        }
    }
    disconnectedCallback() {
        this.#currentContainerRef?.removeAttribute(DATA_IMAGE);
    }
    render() {
        return (h(Host, { key: '024ebb4cbbed0261ed81ebb4e0debdce074c3b1c', "aria-hidden": "true", class: {
                "ch-image--disabled": this.disabled,
                "ch-image--enabled": !this.disabled,
                [`ch-image--${this.type}`]: true
            }, style: this.#image?.styles ?? undefined }));
    }
    static get is() { return "ch-image"; }
    static get originalStyleUrls() {
        return {
            "$": ["image.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["image.css"]
        };
    }
    static get properties() {
        return {
            "containerRef": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "HTMLElement | undefined",
                    "resolved": "HTMLElement",
                    "references": {
                        "HTMLElement": {
                            "location": "global",
                            "id": "global::HTMLElement"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies a reference for the container, in order to update the state of\nthe icon. The reference must be an ancestor of the control.\nIf not specified, the direct parent reference will be used."
                }
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the icon is disabled."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "getImagePathCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    imageSrc: string\n  ) => GxImageMultiState | undefined",
                    "resolved": "(imageSrc: string) => GxImageMultiState",
                    "references": {
                        "GxImageMultiState": {
                            "location": "import",
                            "path": "../../common/types",
                            "id": "src/common/types.ts::GxImageMultiState"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property specifies a callback that is executed when the path the\nimage needs to be resolved."
                }
            },
            "src": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the src for the image."
                },
                "attribute": "src",
                "reflect": false
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "Exclude<ImageRender, \"img\">",
                    "resolved": "\"background\" | \"mask\"",
                    "references": {
                        "Exclude": {
                            "location": "global",
                            "id": "global::Exclude"
                        },
                        "ImageRender": {
                            "location": "import",
                            "path": "../../common/types",
                            "id": "src/common/types.ts::ImageRender"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the image will be rendered."
                },
                "attribute": "type",
                "reflect": false,
                "defaultValue": "\"background\""
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "containerRef",
                "methodName": "containerRefChanged"
            }, {
                "propName": "src",
                "methodName": "srcChanged"
            }];
    }
}
